import { useCallback, useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { AdminApiClient, CompanyCount, ListCompaniesResponse } from '@/features/admin/api/AdminApiClient';
import { CompanyFormDialog } from './CompanyFormDialog';
import { DeleteCompanyDialog } from './DeleteCompanyDialog';

export type CompaniesTabProps = { client: AdminApiClient };

export function CompaniesTab({ client }: CompaniesTabProps) {
  const { t } = useTranslation();
  const { reloadProfile } = useAuth();
  const call = useApiCall<ListCompaniesResponse>({ apiFn: useCallback(() => client.listCompanies(), [client]) });
  const companies = call.data?.companies ?? [];

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'rename'>('create');
  const [selected, setSelected] = useState<CompanyCount | undefined>(undefined);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const refetch = call.retry;
  // Renaming, reassigning or deleting a company can move the signed-in user to another one.
  const handleCompanyChanged = () => {
    refetch();
    void reloadProfile();
  };
  const filtered = companies.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()));

  function openCreate() { setFormMode('create'); setSelected(undefined); setFormOpen(true); }
  function openRename(c: CompanyCount) { setFormMode('rename'); setSelected(c); setFormOpen(true); }
  function openDelete(c: CompanyCount) { setSelected(c); setDeleteOpen(true); }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="min-w-[220px] flex-1"
          placeholder={t('administration.companies.search')}
          aria-label={t('administration.companies.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button variant="default" size="sm" onClick={openCreate}>
          <Plus aria-hidden />
          {t('administration.companies.new')}
        </Button>
      </div>

      {call.error && !call.isLoading && (
        <>
          <StatusCard type="error" message={call.error} />
          <Button variant="outline" size="sm" className="w-fit" onClick={refetch}>
            {t('administration.retry')}
          </Button>
        </>
      )}

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="eyebrow border-b border-border text-left">
              <th className="px-4 py-3 font-semibold">{t('administration.companies.colCompany')}</th>
              <th className="px-4 py-3 font-semibold">{t('administration.companies.colMembers')}</th>
              <th className="px-4 py-3 text-right font-semibold">{t('administration.companies.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {call.isLoading &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="border-b border-border" data-testid="company-skeleton">
                  <td className="px-4 py-3" colSpan={3}><Skeleton className="h-6 w-full" /></td>
                </tr>
              ))}
            {!call.isLoading && !call.error && filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {t('administration.companies.empty')}
                </td>
              </tr>
            )}
            {!call.isLoading &&
              filtered.map((c) => (
                <tr key={c.name} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {t('administration.companies.members', { count: c.userCount })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openRename(c)}>
                      {t('administration.companies.rename')}
                    </Button>
                    <Button variant="ghost-destructive" size="sm" className="ml-1" onClick={() => openDelete(c)}>
                      {t('administration.companies.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <CompanyFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        company={selected?.name}
        client={client}
        onSuccess={handleCompanyChanged}
      />
      {selected && (
        <DeleteCompanyDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          company={selected}
          companies={companies}
          client={client}
          onSuccess={handleCompanyChanged}
        />
      )}
    </div>
  );
}
